import { DatalayerService } from '@analytics/datalayer';
import { AnalyticsUtils } from '@analytics/utils';
import {
  DEFAULT_IMPRESSION_THRESHOLD,
  InViewportConfig,
  InViewportService,
} from '@utils/viewport';
import type { AfterViewInit } from '@angular/core';
import {
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  Input,
} from '@angular/core';
import { PlatformService } from '@core/platform';
import type { ColumnContext } from '@legacy/common-ui-shared-interfaces';
import type { ComponentRendering } from '@core/jss-proxy';
import debounce from 'lodash/debounce';

/**
 * Directive to track impressions of elements when they come into the viewport.
 * It supports tracking two types of impressions: `contentblock-impression` and `button-impression`.
 * The directive pushes impression events to the data layer when the element becomes visible
 * and meets the threshold criteria.
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[trackImpression]',
  providers: [InViewportService],
  standalone: true,
})
export class TrackImpressionDirective implements AfterViewInit {
  /**
   * Type of impression to track. Defaults to `contentblock-impression`.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() trackImpression:
    | 'contentblock-impression'
    | 'button-impression'
    | 'filter-impression' = 'contentblock-impression';

  /**
   * Information about the component being tracked, currently provided by the CMS.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() trackImpressionInfo?: ComponentRendering;

  /**
   * Contextual information about the column or container in which the element resides.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() trackImpressionContext?: ColumnContext;

  /**
   * Optional name for the impression, used for custom identification.
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() trackImpressionName?: string;

  readonly #platformService = inject(PlatformService);
  readonly #inViewport = inject(InViewportService);
  readonly #elementRef = inject(ElementRef);
  readonly #analyticsUtils = inject(AnalyticsUtils);
  readonly #datalayer = inject(DatalayerService);

  readonly #debouncedTrackImpression = debounce(async () => {
    this.#datalayer.pushEvent({
      event: this.trackImpression,
      info: await this.getEventInfo(),
    });
    this.#inViewport.unregister(this.#elementRef.nativeElement);
    this.#observerEffect.destroy();
  }, 300);

  /**
   * Computes the visibility of the element by checking if it is intersecting
   * the viewport and meets the intersection threshold.
   */
  readonly #elementIsVisible = computed(() => {
    const entries = this.#inViewport.entries();
    const elementEntry = entries.find(
      (e) => e.target === this.#elementRef.nativeElement
    );

    if (
      !elementEntry?.isIntersecting ||
      elementEntry.intersectionRatio < DEFAULT_IMPRESSION_THRESHOLD
    ) {
      return false;
    }

    return true;
  });

  /**
   * Effect that triggers when the element becomes visible. If visible, it invokes
   * the debounced method to track the impression.
   */
  readonly #observerEffect = effect(() => {
    if (this.#platformService.isClient() && this.#elementIsVisible()) {
      this.#debouncedTrackImpression();
    }
  });

  /**
   * Lifecycle hook that runs after the view initializes. Registers the element
   * for viewport monitoring if the platform is a client.
   */
  public ngAfterViewInit() {
    if (this.#platformService.isClient()) {
      this.#inViewport.register(
        this.#elementRef.nativeElement,
        new InViewportConfig()
      );
    }
  }

  /**
   * Determines the type of the component
   * @returns name signifying the `type` of the component in the CMS.
   */
  #getComponentType() {
    return this.trackImpressionInfo?.componentName ?? 'unknown-component';
  }

  /**
   * Determines the name of the element with some unruly lookup that we cannot change as of now.
   * Simplifying this breaks all kinds of reports downstream.
   */
  #getElementName() {
    if (this.trackImpressionName) {
      return this.trackImpressionName;
    } else {
      const componentNameFieldValue = (
        this.trackImpressionInfo?.fields?.['componentName'] as any
      )?.value;
      return componentNameFieldValue ?? this.#getComponentType();
    }
  }

  /**
   * Retrieves the event information to be sent with the impression event.
   * This includes details such as component name, position, and type.
   */
  private async getEventInfo() {
    const context = this.trackImpressionContext;
    const componentType = this.#getComponentType();
    const elementName = this.#getElementName();

    switch (this.trackImpression) {
      case 'contentblock-impression':
        return {
          container: context?.name ?? 'main',
          name: elementName,
          type: componentType.toLowerCase(),
          containerposition: context?.containerPosition ?? 0,
          position: context?.index ?? 0,
        };
      case 'button-impression':
      case 'filter-impression': //For backwards-compatiblity with Consumption graph, discussing removal as it's only present ONCE
        return {
          placement: await this.#analyticsUtils.getElementPlacement(
            this.trackImpressionName ?? componentType
          ),
          name: elementName,
        };
      default:
        throw new Error(
          `Unsupported trackImpression type: ${this.trackImpression}`
        );
    }
  }
}
